import { XMLSerializer, DOMParser } from '@xmldom/xmldom';
import assessmentReader from '../../../store/readHelpers/AssessmentReader';
import questionReader from '../../../store/readHelpers/QuestionReader';
import { shouldClearOfflineDataValue } from '../../../services/DrawingUtils';
import getSpreadSheetFileIdStorageKey from '../../../services/SpreadSheetLocalStorageKeys';

export function setAttributes(element, attributes) {
    const propNames = Object.getOwnPropertyNames(attributes);
    for (const name of propNames) {
        element.setAttribute(name, attributes[name]);
    }
}

export function createCandidateResponseNode(doc, values) {
    const elm = doc.createElement('candidateResponse');
    values.forEach((value) => {
        const valueElm = doc.createElement('value');
        const textNode = doc.createTextNode(value);
        valueElm.appendChild(textNode);
        elm.appendChild(valueElm);
    });

    return elm;
}

export function createRespVarElement(doc, data) {
    const elm = doc.createElement('responseVariable');
    const { identifier, cardinality, baseType } = data;
    const attribs = {
        identifier,
        cardinality,
        baseType,
    };

    if (data.choiceSequence) {
        attribs.choiceSequence = data.choiceSequence.join(' ');
    }

    setAttributes(elm, attribs);
    const candRespNode = createCandidateResponseNode(doc, data.userResponse);
    elm.appendChild(candRespNode);

    return elm;
}

export function createGeneralRespVarElement(doc, attributes, values) {
    const elm = doc.createElement('responseVariable');
    setAttributes(elm, attributes);
    const contentElm = createCandidateResponseNode(doc, values);
    elm.appendChild(contentElm);

    return elm;
}

export function stripUnwantedControlCharacters(itemResultString) {
    // eslint-disable-next-line no-control-regex
    return itemResultString.replace(/[\x00-\x08]/g, '');
}

const appendStudentHightlights = (doc, studentHighlights) => {
    appendAnnotations(doc, studentHighlights, 'studentHighlights');
};

const appendQuestionAnnotations = (doc, questionAnnotations) => {
    appendAnnotations(doc, questionAnnotations, 'questionAnnotations');
};

const appendAnnotations = (doc, data, nodeName) => {
    if (Array.isArray(data)) {
        const annotationsString = JSON.stringify(data);
        appendRespVarElement(doc, annotationsString, nodeName, 'single', 'string');
    }
};

const appendRespVarElement = (doc, dataString, identifier, cardinality, baseType) => {
    const respVarElement = createGeneralRespVarElement(
        doc,
        { identifier, cardinality, baseType },
        [dataString]
    );
    doc.firstChild.appendChild(respVarElement);
};

function serializeItemResult(itemResult, includeOfflineData = false) {
    const parser = new DOMParser();
    const serializer = new XMLSerializer();
    const doc = parser.parseFromString('<itemResult></itemResult>');
    const { identifier, sequenceIndex, datestamp, sessionStatus, studentHighlights,
        questionAnnotations, numAttempts, duration, responseIdentifiers } = itemResult;
    const question = assessmentReader.getQuestion(identifier);

    setAttributes(doc.firstChild, { identifier, sequenceIndex, datestamp, sessionStatus });

    appendRespVarElement(doc, numAttempts, 'numAttempts', 'single', 'integer');
    appendRespVarElement(doc, duration, 'duration', 'single', 'duration');
    appendStudentHightlights(doc, studentHighlights);
    appendQuestionAnnotations(doc, questionAnnotations);

    if (Array.isArray(responseIdentifiers)) {
        responseIdentifiers.forEach((item) => {
            // Skip if this is questionAnnotations as it's already added explicitly
            if (item.identifier !== 'questionAnnotations') {
                if (shouldClearOfflineDataValue(item.identifier, includeOfflineData)) {
                    return;
                }
                const userResponse = item.userResponse || [];
                const itemData = {
                    ...item,
                    userResponse,
                };
                const elm = createRespVarElement(doc, itemData);
                doc.firstChild.appendChild(elm);
            }
        });
    }

    const isSpreadSheetTypeQuestion = questionReader.isSpreadSheetQuestion(question);
    if (isSpreadSheetTypeQuestion) {
        const { isSpreadSheetQuestionKey, studentFileIdKey } = getSpreadSheetFileIdStorageKey(identifier);
        // Fallback to the legacy unscoped key for candidates who started the question on an
        // older Player version (which wrote the flat key) before being updated/reloaded mid-session.
        const fileId = localStorage.getItem(studentFileIdKey) || localStorage.getItem('studentFileId');
        if (fileId) {
            const fileIdExists = responseIdentifiers?.some(item => item.identifier === 'FILE_ID');
            if (!fileIdExists) {
                appendRespVarElement(doc, fileId, 'FILE_ID', 'single', 'string');
            }
        }
        localStorage.removeItem(isSpreadSheetQuestionKey);
        localStorage.removeItem(studentFileIdKey);


        // Also clear the legacy unscoped keys so they don't linger indefinitely or get
        // picked up incorrectly by other questions/code paths during the rollout.
        localStorage.removeItem('isSpreadSheetQuestion');
        localStorage.removeItem('studentFileId');
    }

    return stripUnwantedControlCharacters(serializer.serializeToString(doc));
}

export default serializeItemResult;
